/**
 * Faction leaderboard. Source order:
 *  1. contract getAllTeamStats() (current round keys + members per faction)
 *  2. local demo data (keys bought per faction in this browser) when the contract
 *     has nothing yet or is unavailable (demo mode).
 */
import { useEffect, useState } from 'react';
import { FACTIONS } from '../config/factions';
import { getGameContract } from '../utils/contract';

export const LOCAL_FACTION_KEY = 'nt3d_local_faction_keys';

export function recordLocalFactionBuy(faction: number, keys: number) {
  try {
    const m = JSON.parse(localStorage.getItem(LOCAL_FACTION_KEY) || '{}');
    m[faction] = (Number(m[faction]) || 0) + (Number.isFinite(keys) ? keys : 0);
    localStorage.setItem(LOCAL_FACTION_KEY, JSON.stringify(m));
    window.dispatchEvent(new Event('nt3d:faction-stats'));
  } catch { /* ignore */ }
}

function readLocal(): number[] {
  try {
    const m = JSON.parse(localStorage.getItem(LOCAL_FACTION_KEY) || '{}');
    return FACTIONS.map(f => Number(m[f.contractId]) || 0);
  } catch { return FACTIONS.map(() => 0); }
}

const toNum = (v: any) => {
  const n = Number(v ?? 0);
  return n > 1e15 ? n / 1e18 : n; // tolerate 18-decimal key counts
};

interface Row { contractId: number; keys: number; members: number }

const RING_COLORS = ['#46def8', '#3fb8f5', '#4d8cf0', '#6a6cec', '#843ee1', '#a54ce8', '#c955ef', '#f058f6'];
const RC = 2 * Math.PI * 31;

export default function FactionLeaderboard({ playerFaction = 0, refreshTrigger, variant = 'list' }: { playerFaction?: number; refreshTrigger?: number; variant?: 'list' | 'rings' }) {
  const [rows, setRows] = useState<Row[]>([]);
  const [source, setSource] = useState<'onchain' | 'local'>('local');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      let keys: number[] = [];
      let members: number[] = [];
      try {
        const c = await getGameContract();
        if (c) {
          const s = await c.getAllTeamStats();
          keys = Array.from(s.teamKeys || s[0] || []).map(toNum);
          members = Array.from(s.teamMembers || s[1] || []).map((m: any) => Number(m));
        }
      } catch { /* fall through to local */ }
      const hasOnchain = keys.some(k => k > 0);
      const useKeys = hasOnchain ? keys : readLocal();
      if (cancelled) return;
      setSource(hasOnchain ? 'onchain' : 'local');
      setRows(FACTIONS.map((f, i) => ({ contractId: f.contractId, keys: useKeys[i] || 0, members: hasOnchain ? members[i] || 0 : 0 }))
        .sort((a, b) => b.keys - a.keys));
    };
    load();
    const onLocal = () => load();
    window.addEventListener('nt3d:faction-stats', onLocal);
    const t = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(t); window.removeEventListener('nt3d:faction-stats', onLocal); };
  }, [refreshTrigger]);

  const max = Math.max(1, ...rows.map(r => r.keys));
  if (variant === 'rings') {
    // Holo War Room ring gauges: share of keys per faction (palette-graded by rank)
    const total = rows.reduce((a, r) => a + r.keys, 0);
    return (
      <div>
        <div className="holo-rings">
          {rows.map((r, idx) => {
            const f = FACTIONS.find(x => x.contractId === r.contractId)!;
            const share = total > 0 ? (r.keys / total) * 100 : 0;
            const col = RING_COLORS[idx % RING_COLORS.length];
            const mine = playerFaction === r.contractId;
            return (
              <div key={r.contractId} className={`rg ${mine ? 'mine' : ''}`} title={`${f.name}: ${Math.round(r.keys).toLocaleString()} keys`}>
                <svg width="74" height="74" viewBox="0 0 74 74" aria-hidden="true">
                  <circle cx="37" cy="37" r="31" fill="none" stroke="rgba(167,123,255,.14)" strokeWidth="4" />
                  <circle cx="37" cy="37" r="31" fill="none" strokeWidth="4" className="rarc" stroke={col}
                    strokeDasharray={`${((RC * share) / 100).toFixed(1)} ${RC.toFixed(1)}`} transform="rotate(-90 37 37)" style={{ filter: `drop-shadow(0 0 4px ${col})` }} />
                  <circle cx="37" cy="37" r="22" fill="none" stroke="rgba(70,222,248,.2)" strokeDasharray="1 3" />
                  <image href={f.icon} x="23" y="16" width="28" height="28" />
                  <text x="37" y="56" textAnchor="middle" fill="#e3f6f8" fontFamily="JetBrains Mono" fontSize="10">{Math.round(share)}%</text>
                </svg>
                <div className="ln" style={{ color: mine ? '#46def8' : undefined }}>{f.name}{mine ? ' ★' : ''}</div>
              </div>
            );
          })}
        </div>
        <div className="mono" style={{ fontSize: 11, color: '#9fc3c9', marginTop: 8, textAlign: 'right' }}>{source === 'onchain' ? 'this round · on-chain' : 'demo (local) · test FRAME'}</div>
      </div>
    );
  }
  return (
    <div className="glass-panel-3d p-4 rounded-2xl">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-orbitron text-white">Faction Leaderboard</h3>
        <span className="text-[10px] uppercase tracking-widest text-gray-500">{source === 'onchain' ? 'this round' : 'demo (local)'}</span>
      </div>
      <ol className="space-y-2">
        {rows.map((r, idx) => {
          const f = FACTIONS.find(x => x.contractId === r.contractId)!;
          const mine = playerFaction === r.contractId;
          return (
            <li key={r.contractId} className={`flex items-center gap-2 ${mine ? 'text-white' : 'text-gray-300'}`}>
              <span className="w-5 text-xs text-gray-500">{idx + 1}</span>
              <img src={f.icon} alt="" className="w-6 h-6 object-contain" loading="lazy" />
              <span className="w-16 text-sm font-rajdhani" style={{ color: f.color }}>{f.name}{mine ? ' (you)' : ''}</span>
              <div className="flex-1 h-2 rounded bg-white/5 overflow-hidden">
                <div className="h-full rounded" style={{ width: `${(r.keys / max) * 100}%`, background: f.color }} />
              </div>
              <span className="w-20 text-right font-mono text-xs">{Math.round(r.keys).toLocaleString()} keys</span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
